// Waiting banner component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Waiting banner component
 * Displays a waiting message while other players place their characters
 */
class WaitingBanner extends BaseComponent {
  static properties = {
    show: { type: Boolean },
    playerNames: { type: Array, attribute: 'player-names' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .waiting-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 12px 24px;
      background: rgba(0, 0, 0, 0.8);
      color: white;
      border-radius: 8px;
      border: 1px solid #444;
      backdrop-filter: blur(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      font-size: 14px;
      white-space: nowrap;
    }

    .waiting-label {
      font-weight: 600;
    }
  `;

  constructor() {
    super();
    this.show = false;
    /** @type {string[]} */
    this.playerNames = [];
  }

  get _formattedMessage() {
    if (this.playerNames.length === 0) {
      return 'Waiting for other players to place their characters';
    }

    let nameList;
    if (this.playerNames.length === 1) {
      const name = this.playerNames[0];
      if (!name) throw new Error('Expected player name at index 0');
      return `Waiting for ${name} to place their character`;
    } else if (this.playerNames.length === 2) {
      nameList = `${this.playerNames[0]} and ${this.playerNames[1]}`;
    } else {
      // More than 2 players: "Name1, Name2, and Name3"
      const allButLast = this.playerNames.slice(0, -1);
      const last = this.playerNames[this.playerNames.length - 1];
      nameList = `${allButLast.join(', ')}, and ${last}`;
    }

    return `Waiting for ${nameList} to place their characters`;
  }

  _renderHourglassIcon() {
    return html`
      <svg
        width="32"
        height="32"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M5 22h14" />
        <path d="M5 2h14" />
        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
        <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
      </svg>
    `;
  }

  render() {
    if (!this.show) {
      return html``;
    }

    return html`
      <div class="waiting-banner">
        ${this._renderHourglassIcon()}
        <span class="waiting-label">${this._formattedMessage}</span>
      </div>
    `;
  }
}

customElements.define('waiting-banner', WaitingBanner);
